// =========================================================================
// Board List 画面 (W7)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「画面フロー」 main 9 / archive 50 board リスト
//   §「画面フロー > ランダムボタン枯渇時」 「未達成から選ぶ」 派生
//   §「Progress」 BestBadge → 空スロット表示
//
// モード:
//   - 'main'                : main 9 問 (difficulty: easy/normal/hard ラベル区切り)
//   - 'archive'             : archive 50 問 (difficultyBin 1-5 で 5 章区切り)
//   - 'unachieved-silent'   : Silent 未達成のみ
//   - 'unachieved-clean'    : Clean 未達成のみ
//   - 'all'                 : 全 archive (枯渇画面の「全問一覧」)
//
// 各 board カード:
//   - 難易度ラベル + ID
//   - Clear / Clean / Silent 3 つの空スロット → 達成で埋まる
//   - 完了済の board は `--accent-dim`、未完了は `--text-dim`
//
// 触ってはいけない: game/controller.ts (W5) / tutorial/* (W6) / canvas.ts (W3)
// =========================================================================

import type { PlayableBoard } from '../catalog/manifest.js';
import { getBadge, type BestBadge } from '../progress.js';

// -------------------------------------------------------------------------
// 公開型
// -------------------------------------------------------------------------

export type BoardListMode =
  | 'main'
  | 'archive'
  | 'unachieved-silent'
  | 'unachieved-clean'
  | 'all';

export interface BoardListCallbacks {
  onPick: (boardId: string) => void;
  onBack: () => void;
}

export interface BoardListHandle {
  mount(
    container: HTMLElement,
    boards: readonly PlayableBoard[],
    mode: BoardListMode,
    callbacks: BoardListCallbacks,
  ): void;
  destroy(): void;
}

// -------------------------------------------------------------------------
// factory
// -------------------------------------------------------------------------

export function createBoardList(): BoardListHandle {
  let root: HTMLElement | null = null;

  function mount(
    container: HTMLElement,
    boards: readonly PlayableBoard[],
    mode: BoardListMode,
    callbacks: BoardListCallbacks,
  ): void {
    if (root && root.parentNode) {
      root.parentNode.removeChild(root);
    }
    container.innerHTML = '';

    const filtered = filterBoardsByMode(boards, mode);
    const node = build(filtered, mode, callbacks);
    container.appendChild(node);
    root = node;
  }

  function destroy(): void {
    if (root && root.parentNode) {
      root.parentNode.removeChild(root);
    }
    root = null;
  }

  return { mount, destroy };
}

// -------------------------------------------------------------------------
// モード別フィルタ
// -------------------------------------------------------------------------

function filterBoardsByMode(
  boards: readonly PlayableBoard[],
  mode: BoardListMode,
): readonly PlayableBoard[] {
  switch (mode) {
    case 'main':
      return boards.filter((b) => b.source === 'main');
    case 'archive':
      return boards.filter((b) => b.source === 'archive');
    case 'all':
      return boards;
    case 'unachieved-silent':
      return boards.filter((b) => {
        const badge = getBadge(b.id);
        return !badge || !badge.silent;
      });
    case 'unachieved-clean':
      return boards.filter((b) => {
        const badge = getBadge(b.id);
        return !badge || !badge.clean;
      });
  }
}

// -------------------------------------------------------------------------
// レンダリング
// -------------------------------------------------------------------------

function build(
  boards: readonly PlayableBoard[],
  mode: BoardListMode,
  callbacks: BoardListCallbacks,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'nagi-board-list-root';
  wrapper.dataset['mode'] = mode;
  wrapper.style.cssText = [
    'flex: 1',
    'display: flex',
    'flex-direction: column',
    'padding: max(env(safe-area-inset-top), 16px) 16px max(env(safe-area-inset-bottom), 24px)',
    'gap: 16px',
    'min-height: 100vh',
    'box-sizing: border-box',
  ].join(';');

  // ---- header (戻る + タイトル) ----
  wrapper.appendChild(buildHeader(mode, callbacks.onBack));

  // ---- 本体 (グルーピング + カード) ----
  const body = document.createElement('div');
  body.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 18px',
  ].join(';');

  if (boards.length === 0) {
    body.appendChild(buildEmptyState(mode));
  } else {
    const groups = groupBoards(boards, mode);
    for (const group of groups) {
      body.appendChild(buildGroup(group, callbacks.onPick));
    }
  }
  wrapper.appendChild(body);

  return wrapper;
}

function buildHeader(mode: BoardListMode, onBack: () => void): HTMLElement {
  const header = document.createElement('div');
  header.style.cssText = [
    'display: flex',
    'align-items: center',
    'gap: 12px',
    'padding: 4px 0 8px',
    'border-bottom: 1px solid var(--border)',
  ].join(';');

  const backBtn = document.createElement('button');
  backBtn.type = 'button';
  backBtn.textContent = '← タイトル';
  backBtn.style.cssText = [
    'padding: 8px 12px',
    'font-size: var(--font-sm)',
    'background: transparent',
    'border: 1px solid var(--border)',
    'border-radius: var(--radius-sm)',
    'color: var(--text-dim)',
  ].join(';');
  backBtn.addEventListener('click', onBack);

  const title = document.createElement('h2');
  title.textContent = titleFor(mode);
  title.style.cssText = [
    'margin: 0',
    'font-size: var(--font-lg)',
    'font-weight: 500',
    'color: var(--text)',
    'letter-spacing: 0.04em',
  ].join(';');

  header.appendChild(backBtn);
  header.appendChild(title);
  return header;
}

function titleFor(mode: BoardListMode): string {
  switch (mode) {
    case 'main':
      return 'main 9 問';
    case 'archive':
      return 'archive 50 問';
    case 'all':
      return '全問一覧';
    case 'unachieved-silent':
      return 'Silent 未達成';
    case 'unachieved-clean':
      return 'Clean 未達成';
  }
}

function buildEmptyState(mode: BoardListMode): HTMLElement {
  const empty = document.createElement('p');
  empty.style.cssText = [
    'color: var(--text-dim)',
    'font-size: var(--font-sm)',
    'padding: 32px 0',
    'text-align: center',
  ].join(';');
  if (mode === 'unachieved-silent') {
    empty.textContent = 'Silent 未達成の盤面は残っていません。';
  } else if (mode === 'unachieved-clean') {
    empty.textContent = 'Clean 未達成の盤面は残っていません。';
  } else {
    empty.textContent = '表示できる盤面がありません。';
  }
  return empty;
}

// -------------------------------------------------------------------------
// グルーピング (main: difficulty / archive: difficultyBin 章区切り)
// -------------------------------------------------------------------------

interface BoardGroup {
  readonly label: string;
  readonly sub?: string;
  readonly boards: readonly PlayableBoard[];
}

function groupBoards(
  boards: readonly PlayableBoard[],
  mode: BoardListMode,
): readonly BoardGroup[] {
  if (mode === 'main') {
    // difficulty 順: easy → normal → hard
    const order: Array<'easy' | 'normal' | 'hard'> = ['easy', 'normal', 'hard'];
    const groups: BoardGroup[] = [];
    for (const label of order) {
      const items = boards.filter((b) => b.difficultyLabel === label);
      if (items.length === 0) continue;
      groups.push({
        label: difficultyJa(label),
        sub: `${items.length} 問`,
        boards: items,
      });
    }
    return groups;
  }

  if (mode === 'archive' || mode === 'all') {
    // 5 章 × 10
    const groups: BoardGroup[] = [];
    for (let bin = 1; bin <= 5; bin++) {
      const items = boards.filter((b) => b.difficultyBin === bin);
      if (items.length === 0) continue;
      groups.push({
        label: `第 ${bin} 章`,
        sub: `${binLabel(bin)} ・ ${items.length} 問`,
        boards: items,
      });
    }
    // difficultyBin 未設定 (= main 等) は最後に「その他」 にまとめる
    const orphan = boards.filter((b) => b.difficultyBin === undefined);
    if (orphan.length > 0) {
      groups.push({
        label: 'その他',
        sub: `${orphan.length} 問`,
        boards: orphan,
      });
    }
    return groups;
  }

  // unachieved-* : フラット 1 グループ
  return [
    {
      label: titleFor(mode),
      sub: `${boards.length} 問`,
      boards,
    },
  ];
}

function difficultyJa(label: 'easy' | 'normal' | 'hard'): string {
  return label === 'easy' ? 'Easy' : label === 'normal' ? 'Normal' : 'Hard';
}

function binLabel(bin: number): string {
  switch (bin) {
    case 1:
      return 'Easy+';
    case 2:
      return 'Normal';
    case 3:
      return 'Normal+';
    case 4:
      return 'Hard';
    case 5:
      return 'Hard+';
    default:
      return `bin${bin}`;
  }
}

// -------------------------------------------------------------------------
// グループ / カード描画
// -------------------------------------------------------------------------

function buildGroup(group: BoardGroup, onPick: (boardId: string) => void): HTMLElement {
  const section = document.createElement('section');
  section.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 10px',
  ].join(';');

  // 章ラベル
  const header = document.createElement('div');
  header.style.cssText = [
    'display: flex',
    'align-items: baseline',
    'gap: 8px',
    'padding: 0 2px',
  ].join(';');

  const label = document.createElement('h3');
  label.textContent = group.label;
  label.style.cssText = [
    'margin: 0',
    'font-size: var(--font-base)',
    'font-weight: 500',
    'color: var(--text)',
    'letter-spacing: 0.06em',
  ].join(';');
  header.appendChild(label);

  if (group.sub) {
    const sub = document.createElement('span');
    sub.textContent = group.sub;
    sub.style.cssText = [
      'font-size: 11px',
      'color: var(--text-dim)',
      'letter-spacing: 0.04em',
    ].join(';');
    header.appendChild(sub);
  }
  section.appendChild(header);

  // カードグリッド
  const list = document.createElement('ul');
  list.style.cssText = [
    'list-style: none',
    'margin: 0',
    'padding: 0',
    'display: grid',
    'grid-template-columns: repeat(auto-fill, minmax(150px, 1fr))',
    'gap: 8px',
  ].join(';');

  for (const board of group.boards) {
    const li = document.createElement('li');
    const card = buildCard(board, onPick);
    li.appendChild(card);
    list.appendChild(li);
  }

  section.appendChild(list);
  return section;
}

function buildCard(
  board: PlayableBoard,
  onPick: (boardId: string) => void,
): HTMLElement {
  const badge = getBadge(board.id);
  const completed = !!badge && badge.cleared;

  const card = document.createElement('button');
  card.type = 'button';
  card.dataset['boardId'] = board.id;
  card.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'align-items: stretch',
    'gap: 8px',
    'padding: 12px 14px',
    'border-radius: var(--radius-md)',
    'background: var(--bg-elev)',
    `border: 1px solid ${completed ? 'var(--accent-dim)' : 'var(--border)'}`,
    'cursor: pointer',
    'text-align: left',
    'width: 100%',
    'transition: transform var(--t-fast) ease, background var(--t-fast) ease, border-color var(--t-fast) ease',
  ].join(';');

  // 上段: id + 難易度
  const top = document.createElement('div');
  top.style.cssText = [
    'display: flex',
    'justify-content: space-between',
    'align-items: baseline',
    'gap: 6px',
  ].join(';');

  const idEl = document.createElement('span');
  idEl.textContent = formatBoardId(board.id);
  idEl.style.cssText = [
    'font-size: var(--font-base)',
    'font-weight: 500',
    `color: ${completed ? 'var(--accent-dim)' : 'var(--text)'}`,
    'letter-spacing: 0.04em',
  ].join(';');

  const diffEl = document.createElement('span');
  diffEl.textContent = difficultyJa(board.difficultyLabel);
  diffEl.style.cssText = [
    'font-size: 11px',
    'color: var(--text-dim)',
    'letter-spacing: 0.06em',
    'text-transform: uppercase',
  ].join(';');

  top.appendChild(idEl);
  top.appendChild(diffEl);

  // 中段: メトリクス (手数上限 / N)
  const meta = document.createElement('div');
  meta.style.cssText = [
    'display: flex',
    'gap: 8px',
    'font-size: 11px',
    'color: var(--text-dim)',
    'letter-spacing: 0.04em',
  ].join(';');
  meta.appendChild(metaPill(`手数 ${board.N}`));
  meta.appendChild(metaPill(`最短 ${board.shortest}`));

  // 下段: バッジ 3 つ (Clear / Clean / Silent)
  const badges = document.createElement('div');
  badges.style.cssText = [
    'display: flex',
    'gap: 6px',
    'margin-top: 2px',
  ].join(';');
  badges.appendChild(buildBadgeSlot('Clear', badge?.cleared ?? false));
  badges.appendChild(buildBadgeSlot('Clean', badge?.clean ?? false));
  badges.appendChild(
    buildBadgeSlot(
      badge?.silent && typeof badge.bestMoves === 'number'
        ? `Silent ${badge.bestMoves}`
        : 'Silent',
      badge?.silent ?? false,
    ),
  );

  card.appendChild(top);
  card.appendChild(meta);
  card.appendChild(badges);

  card.addEventListener('click', () => {
    onPick(board.id);
  });
  card.addEventListener('mousedown', () => {
    card.style.transform = 'scale(0.97)';
  });
  card.addEventListener('mouseup', () => {
    card.style.transform = '';
  });
  card.addEventListener('mouseleave', () => {
    card.style.transform = '';
  });

  return card;
}

function metaPill(text: string): HTMLElement {
  const el = document.createElement('span');
  el.textContent = text;
  el.style.cssText = [
    'padding: 2px 6px',
    'border-radius: var(--radius-sm)',
    'background: var(--bg-elev-2)',
    'color: var(--text-dim)',
  ].join(';');
  return el;
}

function buildBadgeSlot(label: string, filled: boolean): HTMLElement {
  const el = document.createElement('span');
  el.textContent = label;
  el.style.cssText = [
    'flex: 1',
    'padding: 4px 6px',
    'border-radius: var(--radius-sm)',
    `border: 1px solid ${filled ? 'var(--accent-dim)' : 'var(--border)'}`,
    `background: ${filled ? 'rgba(77, 208, 200, 0.08)' : 'transparent'}`,
    `color: ${filled ? 'var(--accent)' : 'var(--text-dim)'}`,
    'font-size: 11px',
    'text-align: center',
    'letter-spacing: 0.04em',
  ].join(';');
  return el;
}

function formatBoardId(id: string): string {
  // "main-easy-01" / "archive-bin3-07" / "tutorial-01" 等を読みやすく短縮
  // (とりあえずそのまま表示、後で意匠を変えやすいよう helper にしておく)
  return id;
}

// -------------------------------------------------------------------------
// 「Silent 未達成 / Clean 未達成 / 全問一覧」 を選ぶ枯渇画面用 helper
// -------------------------------------------------------------------------

/**
 * spec §「ランダムボタン枯渇時」:
 *   押下 → 未達成 badge 一覧画面:
 *     "Silent 未達成 (xx 問)"
 *     "Clean 未達成 (yy 問)"
 *     "全問一覧"
 *
 * 一覧画面のメニューを別 component に分けるとファイル数が増えるため、ここで
 * 簡易メニューを提供する。main.ts は archive 枯渇時にこちらを mount する。
 */
export interface UnachievedMenuCallbacks {
  onSilent: () => void;
  onClean: () => void;
  onAll: () => void;
  onBack: () => void;
}

export function buildUnachievedMenu(
  boards: readonly PlayableBoard[],
  callbacks: UnachievedMenuCallbacks,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    'flex: 1',
    'display: flex',
    'flex-direction: column',
    'padding: max(env(safe-area-inset-top), 16px) 16px max(env(safe-area-inset-bottom), 24px)',
    'gap: 20px',
    'min-height: 100vh',
    'box-sizing: border-box',
  ].join(';');

  // header
  const header = document.createElement('div');
  header.style.cssText = [
    'display: flex',
    'align-items: center',
    'gap: 12px',
    'padding: 4px 0 8px',
    'border-bottom: 1px solid var(--border)',
  ].join(';');

  const back = document.createElement('button');
  back.type = 'button';
  back.textContent = '← タイトル';
  back.style.cssText = [
    'padding: 8px 12px',
    'font-size: var(--font-sm)',
    'background: transparent',
    'border: 1px solid var(--border)',
    'border-radius: var(--radius-sm)',
    'color: var(--text-dim)',
  ].join(';');
  back.addEventListener('click', callbacks.onBack);

  const title = document.createElement('h2');
  title.textContent = '未達成から選ぶ';
  title.style.cssText = [
    'margin: 0',
    'font-size: var(--font-lg)',
    'font-weight: 500',
    'color: var(--text)',
  ].join(';');

  header.appendChild(back);
  header.appendChild(title);
  wrapper.appendChild(header);

  // 集計
  const silentRemaining = boards.filter((b) => {
    const badge = getBadge(b.id);
    return !badge || !badge.silent;
  }).length;
  const cleanRemaining = boards.filter((b) => {
    const badge = getBadge(b.id);
    return !badge || !badge.clean;
  }).length;

  const menu = document.createElement('nav');
  menu.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: 12px',
  ].join(';');

  menu.appendChild(
    buildMenuItem(
      `Silent 未達成 (${silentRemaining} 問)`,
      '最短手数で閉じきっていない盤面',
      callbacks.onSilent,
    ),
  );
  menu.appendChild(
    buildMenuItem(
      `Clean 未達成 (${cleanRemaining} 問)`,
      '操作後に同値が残ったまま',
      callbacks.onClean,
    ),
  );
  menu.appendChild(
    buildMenuItem('全問一覧', `${boards.length} 問すべて`, callbacks.onAll),
  );

  wrapper.appendChild(menu);
  return wrapper;
}

function buildMenuItem(
  label: string,
  sub: string,
  onClick: () => void,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'align-items: flex-start',
    'gap: 4px',
    'padding: 16px 18px',
    'border-radius: var(--radius-md)',
    'background: var(--bg-elev)',
    'border: 1px solid var(--border)',
    'color: var(--text)',
    'text-align: left',
  ].join(';');

  const labelEl = document.createElement('span');
  labelEl.textContent = label;
  labelEl.style.cssText = [
    'font-size: var(--font-base)',
    'font-weight: 500',
    'color: var(--text)',
  ].join(';');

  const subEl = document.createElement('span');
  subEl.textContent = sub;
  subEl.style.cssText = [
    'font-size: var(--font-sm)',
    'color: var(--text-dim)',
  ].join(';');

  btn.appendChild(labelEl);
  btn.appendChild(subEl);
  btn.addEventListener('click', onClick);
  return btn;
}

// Note for downstream consumers:
//   - BestBadge は progress.ts から re-export しない (= 直接 progress.ts を import)。
//   - PlayableBoard は manifest.ts から流す (= 単一の真実)。
void ({} as BestBadge); // import 使用済みの lint guard (test 用に kept)
